import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Loader2, Search, X, ChevronDown } from 'lucide-react';
import { locationService, LocationResult } from '@services/locationService';

interface LocationPickerProps {
    value: string;
    onChange: (location: { name: string; latitude: number; longitude: number }) => void;
    placeholder?: string;
    className?: string;
    required?: boolean;
    showCurrentLocationButton?: boolean;
}

const LocationPicker = ({
    value,
    onChange,
    placeholder = 'Search for a location...',
    className = '',
    required = false,
    showCurrentLocationButton = true,
}: LocationPickerProps) => {
    const [query, setQuery] = useState(value);
    const [results, setResults] = useState<LocationResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isDetectingLocation, setIsDetectingLocation] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const resultsContainerRef = useRef<HTMLDivElement>(null);

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Update query when value prop changes
    useEffect(() => {
        setQuery(value);
    }, [value]);

    // Lock body scroll when mobile sheet is open
    useEffect(() => {
        if (isOpen && isMobile) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen, isMobile]);

    // Update dropdown position for desktop
    const updatePosition = useCallback(() => {
        if (inputRef.current && !isMobile) {
            const rect = inputRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;
            const dropdownHeight = Math.min(results.length * 72 + 16, 320);

            // Position above if not enough space below
            const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

            setDropdownPosition({
                top: shouldShowAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
    }, [isMobile, results.length]);

    // Handle Resize and Scroll for desktop
    useEffect(() => {
        if (!isOpen || isMobile) return;

        updatePosition();

        const handleScroll = () => {
            updatePosition();
        };

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, updatePosition, results.length, isMobile]);

    // Focus mobile input when sheet opens
    useEffect(() => {
        if (isOpen && isMobile && mobileInputRef.current) {
            setTimeout(() => {
                mobileInputRef.current?.focus();
            }, 300);
        }
    }, [isOpen, isMobile]);

    // Search locations using Nominatim API
    const searchLocations = async (searchQuery: string) => {
        if (!searchQuery.trim() || searchQuery.length < 3) {
            setResults([]);
            return;
        }

        setIsLoading(true);
        try {
            const data = await locationService.searchLocations(searchQuery, 8);
            setResults(data);
            if (!isMobile && data.length > 0) {
                updatePosition();
                setIsOpen(true);
            }
            setSelectedIndex(-1);
        } catch (error) {
            console.error('Error searching locations:', error);
            setResults([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounced search
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = e.target.value;
        setQuery(newQuery);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            searchLocations(newQuery);
        }, 300);
    };

    // Format display name
    const formatDisplayName = locationService.formatDisplayName;

    const handleUseCurrentLocation = async () => {
        if (!navigator.geolocation || isDetectingLocation) return;

        setIsDetectingLocation(true);
        try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    { timeout: 12000, enableHighAccuracy: true, maximumAge: 0 }
                );
            });

            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            let locationName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

            try {
                const locationInfo = await locationService.reverseGeocode(latitude, longitude);
                if (locationInfo) {
                    locationName = formatDisplayName(locationInfo);
                }
            } catch (error) {
                console.warn('Reverse geocode failed for current location:', error);
            }

            setQuery(locationName);
            setResults([]);
            if (isMobile) {
                setIsOpen(false);
            }

            onChange({
                name: locationName,
                latitude,
                longitude,
            });
        } catch (error) {
            console.warn('Current location detection failed:', error);
        } finally {
            setIsDetectingLocation(false);
        }
    };

    // Get short location name for display (City, State, Country format)
    const getShortLocationName = (result: LocationResult): string => {
        const address = result.address;
        if (address) {
            const city = address.city || address.town || address.village || '';
            const state = address.state || '';
            const country = address.country || '';

            // Build parts array with non-empty values
            const parts = [city, state, country].filter(Boolean);

            if (parts.length > 0) {
                return parts.join(', ');
            }
        }
        return formatDisplayName(result);
    };

    // Handle selection
    const handleSelect = (result: LocationResult) => {
        const locationName = formatDisplayName(result);
        setQuery(locationName);
        setResults([]);

        // Animate out then close
        if (isMobile) {
            setIsAnimating(true);
            setTimeout(() => {
                setIsOpen(false);
                setIsAnimating(false);
            }, 200);
        } else {
            setIsOpen(false);
        }

        onChange({
            name: locationName,
            latitude: parseFloat(result.lat),
            longitude: parseFloat(result.lon),
        });
    };

    // Open mobile sheet
    const handleOpenMobileSheet = () => {
        if (isMobile) {
            setIsOpen(true);
            // Search with current query if exists
            if (query.length >= 3) {
                searchLocations(query);
            }
        }
    };

    // Close mobile sheet
    const handleCloseMobileSheet = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsAnimating(false);
            setResults([]);
        }, 200);
    };

    // Close desktop dropdown
    const handleCloseDesktop = () => {
        setTimeout(() => setIsOpen(false), 150);
    };

    // Handle keyboard for desktop
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen || results.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) =>
                    prev < results.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < results.length) {
                    handleSelect(results[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
        }
    };

    // Clear
    const handleClear = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setQuery('');
        setResults([]);
        onChange({ name: '', latitude: 0, longitude: 0 });
        if (!isMobile) {
            inputRef.current?.focus();
        }
    };

    // Render location result item
    const renderResultItem = (result: LocationResult, index: number) => (
        <button
            key={result.place_id}
            type="button"
            onClick={() => handleSelect(result)}
            className={`w-full px-4 py-3.5 text-left flex items-start gap-3 transition-all duration-200 active:scale-[0.98] ${index === selectedIndex
                ? 'bg-primary-600/30 text-white'
                : 'text-white/90 hover:bg-white/10 active:bg-white/15'
                }`}
        >
            <div className="shrink-0 w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <MapPin size={18} className="text-primary-400" />
            </div>
            <div className="min-w-0 flex-1 py-0.5">
                <p className="text-sm font-medium leading-tight">
                    {getShortLocationName(result)}
                </p>
                <p className="text-xs text-white/50 truncate mt-1 leading-tight">
                    {result.display_name}
                </p>
            </div>
        </button>
    );

    // Desktop dropdown
    const renderDesktopDropdown = () => {
        // Don't render if mobile, not open, or position not yet calculated
        if (isMobile || !isOpen || dropdownPosition.width === 0) return null;

        return createPortal(
            <div
                ref={resultsContainerRef}
                className="fixed z-[9999] bg-cosmic-900/98 backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width,
                    maxHeight: '320px',
                }}
                onMouseDown={(e) => e.preventDefault()}
            >
                <div className="overflow-y-auto max-h-[320px] overscroll-contain">
                    {results.length > 0 ? (
                        <div className="divide-y divide-white/5">
                            {results.map((result, index) => renderResultItem(result, index))}
                        </div>
                    ) : (
                        <div className="p-6 text-center">
                            {isLoading ? (
                                <div className="flex flex-col items-center gap-3">
                                    <Loader2 size={24} className="animate-spin text-primary-400" />
                                    <p className="text-white/50 text-sm">Searching locations...</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <MapPin size={24} className="text-white/30" />
                                    <p className="text-white/50 text-sm">No locations found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>,
            document.body
        );
    };

    // Mobile bottom sheet
    const renderMobileSheet = () => {
        if (!isMobile || !isOpen) return null;

        return createPortal(
            <div
                className={`fixed inset-0 z-[10000] transition-opacity duration-200 ${isAnimating ? 'opacity-0' : 'opacity-100'
                    }`}
            >
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={handleCloseMobileSheet}
                />

                {/* Bottom Sheet */}
                <div
                    className={`absolute bottom-0 left-0 right-0 bg-cosmic-900 rounded-t-3xl overflow-hidden transition-transform duration-300 ease-out ${isAnimating ? 'translate-y-full' : 'translate-y-0'
                        }`}
                    style={{
                        maxHeight: 'calc(100vh - 60px)',
                        paddingBottom: 'env(safe-area-inset-bottom, 20px)'
                    }}
                >
                    {/* Handle bar */}
                    <div className="flex justify-center pt-3 pb-2">
                        <div className="w-10 h-1 rounded-full bg-white/20" />
                    </div>

                    {/* Header */}
                    <div className="px-4 pb-3">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-white">Select Location</h3>
                            <button
                                type="button"
                                onClick={handleCloseMobileSheet}
                                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-all active:scale-95"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative">
                            <input
                                ref={mobileInputRef}
                                type="text"
                                value={query}
                                onChange={handleInputChange}
                                placeholder={placeholder}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                className="w-full py-3.5 pl-12 pr-12 bg-white/10 border border-white/15 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all text-base"
                            />
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400">
                                {isLoading ? (
                                    <Loader2 size={20} className="animate-spin" />
                                ) : (
                                    <Search size={20} />
                                )}
                            </div>
                            {query && (
                                <button
                                    type="button"
                                    onClick={() => handleClear()}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/20 transition-all active:scale-90"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Results */}
                    <div
                        className="overflow-y-auto overscroll-contain"
                        style={{ maxHeight: 'calc(100vh - 240px)' }}
                    >
                        {results.length > 0 ? (
                            <div className="divide-y divide-white/5 pb-4">
                                {results.map((result, index) => renderResultItem(result, index))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                {isLoading ? (
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                                            <Loader2 size={24} className="animate-spin text-primary-400" />
                                        </div>
                                        <p className="text-white/50 text-sm">Searching locations...</p>
                                    </div>
                                ) : query.length < 3 ? (
                                    <div className="flex flex-col items-center gap-3 px-8">
                                        <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                                            <Search size={24} className="text-white/30" />
                                        </div>
                                        <div>
                                            <p className="text-white/60 text-sm font-medium">Search for your location</p>
                                            <p className="text-white/40 text-xs mt-1">Type at least 3 characters to search</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-3 px-8">
                                        <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                                            <MapPin size={24} className="text-white/30" />
                                        </div>
                                        <div>
                                            <p className="text-white/60 text-sm font-medium">No locations found</p>
                                            <p className="text-white/40 text-xs mt-1">Try a different search term</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            document.body
        );
    };

    return (
        <div className={`relative ${className}`}>
            {/* Main Input - Desktop: regular input, Mobile: trigger button */}
            <div className="relative">
                {isMobile ? (
                    // Mobile: Trigger button that opens bottom sheet
                    <button
                        type="button"
                        onClick={handleOpenMobileSheet}
                        className="w-full py-3.5 pl-12 pr-12 bg-white/5 border border-white/20 rounded-2xl text-left transition-all active:scale-[0.99] focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                        <span className={`text-sm ${query ? 'text-white' : 'text-white/40'}`}>
                            {query || placeholder}
                        </span>
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400">
                            <MapPin size={18} />
                        </div>
                        <div
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40"
                            onClick={(e) => {
                                if (query) {
                                    e.stopPropagation();
                                    handleClear();
                                }
                            }}
                        >
                            {query ? (
                                <X size={18} className="cursor-pointer hover:text-white/70 transition-colors" />
                            ) : (
                                <ChevronDown size={18} />
                            )}
                        </div>
                    </button>
                ) : (
                    // Desktop: Regular text input
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onFocus={() => {
                            // Calculate position first, then show dropdown
                            updatePosition();
                            if (results.length > 0) {
                                setIsOpen(true);
                            } else if (query.length >= 3) {
                                searchLocations(query);
                            }
                        }}
                        onBlur={handleCloseDesktop}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        required={required}
                        autoComplete="off"
                        className="w-full py-3 md:py-4 pl-12 pr-12 bg-white/5 border border-white/20 rounded-2xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-transparent transition-all text-sm md:text-base font-sans"
                    />
                )}

                {!isMobile && (
                    <>
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400">
                            {isLoading ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : (
                                <Search size={18} />
                            )}
                        </div>

                        {query && (
                            <button
                                type="button"
                                onClick={() => handleClear()}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Dropdowns */}
            {renderDesktopDropdown()}
            {renderMobileSheet()}

            {showCurrentLocationButton && (
                <button
                    type="button"
                    onClick={handleUseCurrentLocation}
                    disabled={isDetectingLocation || !navigator.geolocation}
                    className="mt-3 w-full py-2.5 bg-white/[0.08] hover:bg-white/[0.12] disabled:opacity-60 disabled:cursor-not-allowed border border-white/20 text-white text-sm font-medium rounded-xl transition-colors"
                >
                    {isDetectingLocation ? 'Requesting location permission...' : 'Use Current Location'}
                </button>
            )}
        </div>
    );
};

export default LocationPicker;
